import React from 'react';
import { toast } from 'sonner';
import { Eye, RotateCcw } from 'lucide-react';
import { adminApi } from '../../../lib/adminAuth';
import DocPreviewDialog from './DocPreviewDialog';
import { errMsg } from './docUtils';

const when = (v) => (v ? new Date(v).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '');

export default function ContractHistory({ doc, onRestored, locked }) {
  const [versions, setVersions] = React.useState([]);
  const [preview, setPreview] = React.useState(null);
  React.useEffect(() => {
    adminApi.get(`/api/admin/contracts/${doc._id}/versions`).then((r) => setVersions(r.data)).catch(() => {});
  }, [doc._id, doc.version]);
  const restore = async (v) => {
    if (!window.confirm(`Restore version ${v}? The current content is kept as a version too.`)) return;
    try { const r = await adminApi.post(`/api/admin/contracts/${doc._id}/versions/${v}/restore`); toast.success(`Version ${v} restored`); onRestored(r.data); }
    catch (e) { toast.error(errMsg(e, 'Could not restore')); }
  };
  return (
    <div className="space-y-6" data-testid="contract-history">
      <div>
        <p className="text-sm font-semibold text-slate-800 mb-2">Versions <span className="text-xs font-normal text-slate-500">· current v{doc.version}</span></p>
        {versions.length === 0 && <p className="text-xs text-slate-500">No earlier versions yet. Every saved change keeps the previous copy here.</p>}
        <div className="space-y-1.5">
          {versions.map((v) => (
            <div key={v._id} className="flex items-center gap-3 p-2.5 rounded-lg border border-slate-200 bg-white text-xs" data-testid={`contract-version-${v.version}`}>
              <span className="font-bold text-slate-700 w-8">v{v.version}</span>
              <span className="flex-1 text-slate-500 truncate">{when(v.saved_at)} · {v.saved_by_name} · changed {(v.changed_fields || []).join(', ')}</span>
              <button type="button" onClick={() => setPreview(v.version)} className="p-1.5 rounded hover:bg-slate-100" data-testid={`contract-version-preview-${v.version}`} aria-label="Preview"><Eye className="w-3.5 h-3.5" /></button>
              {!locked && <button type="button" onClick={() => restore(v.version)} className="p-1.5 rounded hover:bg-slate-100" data-testid={`contract-version-restore-${v.version}`} aria-label="Restore"><RotateCcw className="w-3.5 h-3.5" /></button>}
            </div>
          ))}
        </div>
      </div>
      <div>
        <p className="text-sm font-semibold text-slate-800 mb-2">Activity</p>
        <ol className="border-l-2 border-fuchsia-100 pl-4 space-y-2">
          {[...(doc.history || [])].reverse().map((h, i) => (
            <li key={i} className="text-xs text-slate-600"><b className="capitalize text-slate-800">{h.event.replace('_', ' ')}</b> · {when(h.at)}{h.note ? ` · ${h.note}` : ''}</li>
          ))}
        </ol>
        {doc.signature && (
          <div className="mt-3 p-3 rounded-lg bg-emerald-50 text-xs text-emerald-800" data-testid="contract-signature-info">
            Signed by <b>{doc.signature.name}</b>{doc.signature.designation ? `, ${doc.signature.designation}` : ''} ({doc.signature.method}) on {when(doc.signature.signed_at)} · IP {doc.signature.ip || '—'}
          </div>
        )}
      </div>
      <DocPreviewDialog open={!!preview} onClose={() => setPreview(null)} title={`Version ${preview}`}
        previewUrl={preview ? `/api/admin/contracts/${doc._id}/versions/${preview}/preview` : null} />
    </div>
  );
}
